import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChartBarIcon,
  ClockIcon,
  TrophyIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  EyeIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const TaskAnalytics = () => {
  const [timeRange, setTimeRange] = useState('thisMonth');

  // Analytics data
  const analytics = {
    thisMonth: {
      tasksCompleted: 28,
      tasksInProgress: 12,
      tasksPending: 8,
      totalHoursLogged: 156.5,
      averageCompletionTime: 2.3,
      onTimeDelivery: 92,
      productivityScore: 87,
      efficiency: 94,
    },
    lastMonth: {
      tasksCompleted: 22,
      tasksInProgress: 15,
      tasksPending: 6,
      totalHoursLogged: 134.2,
      averageCompletionTime: 2.8,
      onTimeDelivery: 88,
      productivityScore: 82,
      efficiency: 89,
    },
    thisWeek: {
      tasksCompleted: 8,
      tasksInProgress: 4,
      tasksPending: 2,
      totalHoursLogged: 42.5,
      averageCompletionTime: 2.1,
      onTimeDelivery: 95,
      productivityScore: 91,
      efficiency: 96,
    },
  };

  // Performance trends
  const performanceTrends = [
    { period: 'Week 1', completed: 6, hours: 32, efficiency: 89 },
    { period: 'Week 2', completed: 8, hours: 38, efficiency: 92 },
    { period: 'Week 3', completed: 7, hours: 35, efficiency: 88 },
    { period: 'Week 4', completed: 9, hours: 42, efficiency: 95 },
  ];

  // Top projects by activity
  const topProjects = [
    {
      id: 1,
      name: "Healthcare Compliance",
      tasksCompleted: 8,
      hoursSpent: 45.5,
      efficiency: 92,
      progress: 67,
      priority: "High",
    },
    {
      id: 2,
      name: "Quality Management",
      tasksCompleted: 12,
      hoursSpent: 68.2,
      efficiency: 89,
      progress: 82,
      priority: "Medium",
    },
    {
      id: 3,
      name: "Protocol Updates",
      tasksCompleted: 6,
      hoursSpent: 28.8,
      efficiency: 94,
      progress: 71,
      priority: "High",
    },
    {
      id: 4,
      name: "Staff Development",
      tasksCompleted: 4,
      hoursSpent: 24.3,
      efficiency: 87,
      progress: 45,
      priority: "Medium",
    },
  ];

  // Task categories analysis
  const taskCategories = [
    { category: "Health & Safety", count: 8, percentage: 32, avgTime: 3.2 },
    { category: "Quality Assurance", count: 6, percentage: 24, avgTime: 2.8 },
    { category: "Training", count: 5, percentage: 20, avgTime: 2.1 },
    { category: "Maintenance", count: 4, percentage: 16, avgTime: 1.9 },
    { category: "Administration", count: 2, percentage: 8, avgTime: 1.5 },
  ];

  // Recent achievements
  const achievements = [
    {
      id: 1,
      title: "Perfect Week",
      description: "Completed all scheduled tasks on time",
      date: "2025-07-21",
      icon: TrophyIcon,
      color: "text-yellow-600 bg-yellow-100",
    },
    {
      id: 2,
      title: "Efficiency Master",
      description: "Maintained 95%+ efficiency for 2 weeks",
      date: "2025-07-18",
      icon: ArrowTrendingUpIcon,
      color: "text-green-600 bg-green-100",
    },
    {
      id: 3,
      title: "Quality Champion",
      description: "Zero revision requests for 15 tasks",
      date: "2025-07-15",
      icon: ChartBarIcon,
      color: "text-blue-600 bg-blue-100",
    },
  ];

  const currentData = analytics[timeRange];
  const previousData = timeRange === 'thisMonth' ? analytics.lastMonth : 
                      timeRange === 'thisWeek' ? analytics.thisMonth : analytics.lastMonth;

  const calculateChange = (current, previous) => {
    const change = ((current - previous) / previous) * 100;
    return { value: Math.abs(change).toFixed(1), isPositive: change >= 0 };
  };

  const getMetricColor = (value, threshold = 80) => {
    return value >= threshold ? 'text-green-600' : value >= 60 ? 'text-yellow-600' : 'text-red-600';
  };

  const formatHours = (hours) => {
    return `${hours.toFixed(1)}h`;
  };

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 sm:py-6 space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Task Analytics</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                Track your productivity and performance metrics
              </p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
              <div className="relative">
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-auto"
                >
                  <option value="thisWeek">This Week</option>
                  <option value="thisMonth">This Month</option>
                  <option value="lastMonth">Last Month</option>
                </select>
                <FunnelIcon className="h-4 w-4 absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Tasks Completed</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{currentData.tasksCompleted}</p>
              </div>
              <ChartBarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
            </div>
            <div className="mt-2 flex items-center text-xs sm:text-sm">
              {(() => {
                const change = calculateChange(currentData.tasksCompleted, previousData.tasksCompleted);
                return (
                  <span className={`flex items-center ${change.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {change.isPositive ? (
                      <ArrowTrendingUpIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    ) : (
                      <ArrowTrendingDownIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    )}
                    {change.value}% from last period
                  </span>
                );
              })()}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Hours Logged</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{formatHours(currentData.totalHoursLogged)}</p>
              </div>
              <ClockIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
            </div>
            <div className="mt-2 flex items-center text-xs sm:text-sm">
              {(() => {
                const change = calculateChange(currentData.totalHoursLogged, previousData.totalHoursLogged);
                return (
                  <span className={`flex items-center ${change.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {change.isPositive ? (
                      <ArrowTrendingUpIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    ) : (
                      <ArrowTrendingDownIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    )}
                    {change.value}% from last period
                  </span>
                );
              })()}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Productivity Score</p>
                <p className={`text-xl sm:text-2xl font-bold ${getMetricColor(currentData.productivityScore)}`}>
                  {currentData.productivityScore}%
                </p>
              </div>
              <TrophyIcon className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-600" />
            </div>
            <div className="mt-2 flex items-center text-xs sm:text-sm">
              {(() => {
                const change = calculateChange(currentData.productivityScore, previousData.productivityScore);
                return (
                  <span className={`flex items-center ${change.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {change.isPositive ? (
                      <ArrowTrendingUpIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    ) : (
                      <ArrowTrendingDownIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    )}
                    {change.value}% from last period
                  </span>
                );
              })()}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">On-Time Delivery</p>
                <p className={`text-xl sm:text-2xl font-bold ${getMetricColor(currentData.onTimeDelivery, 90)}`}>
                  {currentData.onTimeDelivery}%
                </p>
              </div>
              <CalendarDaysIcon className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
            </div>
            <div className="mt-2 flex items-center text-xs sm:text-sm">
              {(() => {
                const change = calculateChange(currentData.onTimeDelivery, previousData.onTimeDelivery);
                return (
                  <span className={`flex items-center ${change.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {change.isPositive ? (
                      <ArrowTrendingUpIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    ) : (
                      <ArrowTrendingDownIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    )}
                    {change.value}% from last period
                  </span>
                );
              })()}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
          {/* Performance Trends */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-4 sm:p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Performance Trends</h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Weekly progress overview</p>
            </div>
            <div className="p-4 sm:p-6">
              <div className="space-y-4">
                {performanceTrends.map((trend, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 space-y-1 sm:space-y-0">
                        <span className="text-sm font-medium text-gray-900">{trend.period}</span>
                        <div className="flex items-center space-x-2 sm:space-x-4 text-xs sm:text-sm text-gray-600">
                          <span>{trend.completed} tasks</span>
                          <span>{formatHours(trend.hours)}</span>
                          <span className={getMetricColor(trend.efficiency)}>{trend.efficiency}%</span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${trend.efficiency}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Task Categories */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-4 sm:p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Task Categories</h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Distribution by category</p>
            </div>
            <div className="p-4 sm:p-6">
              <div className="space-y-4">
                {taskCategories.map((category, index) => (
                  <div key={index}>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 space-y-1 sm:space-y-0">
                      <span className="text-sm font-medium text-gray-900">{category.category}</span>
                      <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-600">
                        <span>{category.count} tasks</span>
                        <span>{formatHours(category.avgTime)} avg</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${category.percentage}%` }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{category.percentage}% of total tasks</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Top Projects */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-4 sm:p-6 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Top Projects</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">Most active projects</p>
                </div>
                <UserGroupIcon className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            <div className="p-4 sm:p-6">
              <div className="space-y-4">
                {topProjects.map((project) => (
                  <div key={project.id} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 space-y-2 sm:space-y-0">
                      <div className="flex-1">
                        <Link
                          to={`/projects/${project.id}`}
                          className="font-medium text-gray-900 hover:text-blue-600 transition-colors"
                        >
                          {project.name}
                        </Link>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-1 text-xs sm:text-sm text-gray-600">
                          <span>{project.tasksCompleted} tasks</span>
                          <span>{formatHours(project.hoursSpent)}</span>
                          <span className={getMetricColor(project.efficiency)}>{project.efficiency}% efficiency</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 self-start">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          project.priority === 'High' ? 'text-red-600 bg-red-50' : 'text-yellow-600 bg-yellow-50'
                        }`}>
                          {project.priority}
                        </span>
                        <Link
                          to={`/projects/${project.id}`}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                    <div className="mb-2">
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                        <span>Progress</span>
                        <span>{project.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${project.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Achievements */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Recent Achievements</h3>
                  <p className="text-sm text-gray-600 mt-1">Your latest milestones</p>
                </div>
                <TrophyIcon className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {achievements.map((achievement) => (
                  <div key={achievement.id} className="flex items-start space-x-4">
                    <div className={`p-2 rounded-lg ${achievement.color}`}>
                      <achievement.icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{achievement.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{achievement.description}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        {new Date(achievement.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 pt-4 border-t">
                <Link
                  to="/tasks/achievements"
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  View All Achievements →
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Metrics */}
        <div className="mt-6 sm:mt-8 bg-white rounded-lg shadow-sm border">
          <div className="p-4 sm:p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Additional Metrics</h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Detailed performance indicators</p>
          </div>
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-gray-900">{currentData.averageCompletionTime}</div>
                <div className="text-xs sm:text-sm text-gray-600">Avg Completion Time (days)</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-gray-900">{currentData.efficiency}%</div>
                <div className="text-xs sm:text-sm text-gray-600">Overall Efficiency</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-gray-900">{currentData.tasksInProgress}</div>
                <div className="text-xs sm:text-sm text-gray-600">Tasks In Progress</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-gray-900">{currentData.tasksPending}</div>
                <div className="text-xs sm:text-sm text-gray-600">Tasks Pending</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default TaskAnalytics;
